import { useState } from 'react';
import { useParams } from 'react-router';

import { faThumbsUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    IonButton,
    IonCol,
    IonContent,
    IonFab,
    IonGrid,
    IonLabel,
    IonPage,
    IonRow,
    IonText,
    IonThumbnail,
    useIonAlert,
    useIonToast,
} from '@ionic/react';
import { useAtomValue } from 'jotai';
import { filter, flow, map, size, some, sortBy, uniq } from 'lodash/fp';

import '../../pages/validation/Leave.scss';

import { FabContact } from '../../components/FabContact';
import { Header } from '../../components/Header';
import { IosHeader } from '../../components/IosHeader';
import { useCompanies, useEmployees, useLeaves } from '../../hooks/useSwr';
import { EmployeeLeaveItem } from '../../pages/MyLeave';
import { displayMatrix } from '../../pages/validation/ChooseLeaveState';
import { Leave, OdooEmployee } from '../../types/models';
import { defaultCatchOdooError, usersAtom } from '../../utils/Global';
import { nowToOdooDatetime } from '../../utils/date';
import odoo from '../../utils/odoo';
import { LeaveState, getMetaData } from '../../utils/odooUtils';

export const ValidationLeave: React.FC = () => {
    const [presentToast] = useIonToast();

    const { state } = useParams<{ state: LeaveState }>();

    if (!state || !Object.values(LeaveState).includes(state)) {
        return null;
    }

    const { standardizedOdooLeaves: leaves, mutate } = useLeaves([['state', '=', state]]);
    const title = displayMatrix[state].title || 'Validation Congés';

    type ModalEventType = { adherent_note: string };

    const { mainUser } = useAtomValue(usersAtom);

    const { normalize: employees, data: employeesData } = useEmployees();
    const { normalize: companies } = useCompanies();

    const [validationConfirmAlert] = useIonAlert();

    const [leaveChecked, setLeaveChecked] = useState<number[]>([]);

    const onSelectionChange = (leaveIds: number[], checked: boolean) => {
        setLeaveChecked(
            checked ? [...leaveChecked, ...leaveIds] : filter((id: number) => !leaveIds.includes(id), leaveChecked),
        );
    };

    const availableCompanies = flow(map('company_id[0]'), uniq)(employeesData) as number[];

    const filteredCompanies = filter((company) => availableCompanies.includes(company.id), companies);

    const getFilteredLeavesByEmployee = (employeeId: number) =>
        filter((leave) => leave.employee_id === employeeId, leaves);

    const processLeave = async (leaveId: number | number[], refuse: boolean, ev: ModalEventType) => {
        try {
            await odoo.write('hr.madper.app.leave', leaveId, {
                user_adherent_id: mainUser!.uid,
                user_adherent_validation_date: nowToOdooDatetime(),
                refuse,
                metadata: JSON.stringify(await getMetaData(false)),
                ...ev,
            });
            setLeaveChecked([]);
            mutate();
        } catch (error: any) {
            defaultCatchOdooError(error, presentToast);
        }
    };

    if (!employees) {
        return null;
    }

    const checkStateForBtn = [LeaveState.WaitValidation, LeaveState.AdherentRefusal, LeaveState.AdherentValidation];

    return (
        <IonPage id="validation-page">
            <IosHeader title={title} backText="Retour" backLocation="/choose-leaves" />
            <IonContent className="bg-neutral-100" style={{ '--padding-bottom': '60px' }}>
                <Header title={title} backText="Retour" backLocation="/choose-leaves" />
                {map(
                    (company) => (
                        <div key={company.id}>
                            {/* TODO fix for multiple company */}
                            {(state === LeaveState.GeiqValidation || state === LeaveState.GeiqRefusal) &&
                                (company?.phone || company?.email) && <FabContact company={company} />}

                            {map(
                                (employee: OdooEmployee) => (
                                    <IonGrid
                                        className="ion-no-padding mb-4 pt-5 grid-validation-infos-d"
                                        key={employee.id}
                                    >
                                        <div className="p-5 pb-2">
                                            <IonRow className="pb-4 w-full">
                                                <IonCol size="auto">
                                                    <IonThumbnail className="w-28 h-28">
                                                        <img
                                                            className="rounded-lg h-full"
                                                            alt="Employee avatar"
                                                            src={
                                                                employee.image_128
                                                                    ? `data:image/jpeg;base64,${employee.image_128}`
                                                                    : 'https://ionicframework.com/docs/img/demos/avatar.svg'
                                                            }
                                                        />
                                                    </IonThumbnail>
                                                </IonCol>
                                                <IonCol size="8" className="pl-4">
                                                    <IonLabel className="text-2xl font-bold text-black">
                                                        {employee.name}
                                                    </IonLabel>
                                                    <IonText className="text-gray-500">
                                                        <p>{employee.best_phone}</p>
                                                    </IonText>
                                                </IonCol>
                                            </IonRow>
                                        </div>
                                        <div className="xl:flex xl:flex-wrap">
                                            {map(
                                                (leave: Leave) => (
                                                    <EmployeeLeaveItem
                                                        key={leave.id + '_' + leave.employee_id}
                                                        leave={leave}
                                                        onSelectionChange={
                                                            (checkStateForBtn.includes(state) && onSelectionChange) ||
                                                            undefined
                                                        }
                                                        onSelectionFullCard
                                                        checked={leaveChecked.includes(leave.id)}
                                                        nbChecked={leaveChecked.length}
                                                        onValidate={
                                                            (checkStateForBtn.includes(state) &&
                                                                (() => {
                                                                    validationConfirmAlert({
                                                                        header: 'Voulez-vous accepter ou refuser ce congé ?',
                                                                        inputs: [
                                                                            {
                                                                                value: leave.adherent_note,
                                                                                type: 'textarea',
                                                                                placeholder: 'Commentaire (optionnel)',
                                                                                name: 'adherent_note',
                                                                            },
                                                                        ],
                                                                        buttons: [
                                                                            {
                                                                                text: 'Refuser',
                                                                                role: 'refuse',
                                                                                cssClass: 'refuse-button',
                                                                                handler: async (ev: ModalEventType) => {
                                                                                    await processLeave(
                                                                                        leave.id,
                                                                                        true,
                                                                                        ev,
                                                                                    );
                                                                                },
                                                                            },
                                                                            {
                                                                                text: 'Valider',
                                                                                role: 'accept',
                                                                                cssClass: 'accept-button',
                                                                                handler: async (ev: ModalEventType) => {
                                                                                    await processLeave(
                                                                                        leave.id,
                                                                                        false,
                                                                                        ev,
                                                                                    );
                                                                                },
                                                                            },
                                                                        ],
                                                                    });
                                                                })) ||
                                                            undefined
                                                        }
                                                    />
                                                ),
                                                getFilteredLeavesByEmployee(employee.id),
                                            )}
                                        </div>
                                    </IonGrid>
                                ),
                                flow(
                                    filter(
                                        (employee: OdooEmployee) =>
                                            employee.company_id[0] === company.id &&
                                            some((leave) => leave.employee_id === employee.id, leaves),
                                    ),
                                    sortBy('name'),
                                )(employeesData),
                            )}

                            <div className="madper-button">
                                <IonFab
                                    className={`fab-process-leave ${leaveChecked.length > 0 ? 'show-fab' : ''}`}
                                    slot="fixed"
                                    vertical="bottom"
                                    horizontal="end"
                                >
                                    <IonButton
                                        shape="round"
                                        type="submit"
                                        expand="block"
                                        className="normal-case"
                                        size="large"
                                        onClick={() => {
                                            validationConfirmAlert({
                                                header: 'Voulez-vous accepter ou refuser les congés sélectionnés ?',
                                                buttons: [
                                                    {
                                                        text: 'Refuser',
                                                        role: 'refuse',
                                                        cssClass: 'refuse-button',
                                                        handler: async (ev: ModalEventType) => {
                                                            await processLeave(leaveChecked, true, ev);
                                                        },
                                                    },
                                                    {
                                                        text: 'Valider',
                                                        role: 'accept',
                                                        cssClass: 'accept-button',
                                                        handler: async (ev: ModalEventType) => {
                                                            await processLeave(leaveChecked, false, ev);
                                                        },
                                                    },
                                                ],
                                            });
                                        }}
                                    >
                                        Gérer
                                    </IonButton>
                                </IonFab>
                            </div>
                            {!size(leaves) && (
                                <div className="flex h-screen place-items-center justify-center ">
                                    <p className="text-center font-bold">Plus aucun congé à valider</p>
                                    <FontAwesomeIcon icon={faThumbsUp} size="3x" transform={{ y: -3, x: 4 }} />
                                </div>
                            )}
                        </div>
                    ),
                    filteredCompanies,
                )}
            </IonContent>
        </IonPage>
    );
};

export default ValidationLeave;
